import { apiClient } from './apiClient';
import { UserAchievementProgress, ClanAchievementProgress, AchievementProgress } from '../types/achievement';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  gameType: string;
  rarity: string;
  isActive: boolean;
  conditions: {
    type: string;
    count: number;
    missionId?: string;
    consecutively?: boolean;
    timeframe?: string;
    requiredAchievementId?: string;
  };
  iconUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAchievementRequest {
  name: string;
  description: string;
  gameType: string;
  rarity: string;
  conditions: {
    type: string;
    count: number;
    missionId?: string;
    consecutively?: boolean;
    timeframe?: string;
    requiredAchievementId?: string;
  };
  iconUrl?: string;
}

// User Achievements
export const getUserAchievements = async (): Promise<Achievement[]> => {
  return await apiClient.get<Achievement[]>('/api/userachievement');
};

export const getActiveUserAchievements = async (): Promise<Achievement[]> => {
  return await apiClient.get<Achievement[]>('/api/userachievement/active');
};


export const createUserAchievementWithFile = async (formData: FormData): Promise<Achievement> => {
  return await apiClient.postFormData<Achievement>('/api/userachievement', formData);
};


export const updateUserAchievementWithFile = async (id: string, formData: FormData): Promise<Achievement> => {
  return await apiClient.putFormData<Achievement>(`/api/userachievement/${id}`, formData);
};

export const deleteUserAchievement = async (id: string): Promise<void> => {
  await apiClient.delete<void>(`/api/userachievement/${id}`);
};

export const toggleUserAchievementStatus = async (id: string, isActive: boolean): Promise<void> => {
  await apiClient.patch<void>(`/api/userachievement/${id}/status`, isActive);
};

export const getUserProgress = async (userId: string): Promise<UserAchievementProgress[]> => {
  return await apiClient.get<UserAchievementProgress[]>(`/api/userachievement/user/${userId}/progress`);
};

export const getUserIncompleteProgress = async (userId: string): Promise<UserAchievementProgress[]> => {
  return await apiClient.get<UserAchievementProgress[]>(`/api/userachievement/user/${userId}/progress/incomplete`);
};

export const getAchievementsByIds = async (achievementIds: string[]): Promise<Achievement[]> => {
  return await apiClient.post<Achievement[]>('/api/userachievement/by-ids', achievementIds);
};

// Clan Achievements
export const getClanAchievements = async (): Promise<Achievement[]> => {
  return await apiClient.get<Achievement[]>('/api/clanachievement');
};

export const getActiveClanAchievements = async (): Promise<Achievement[]> => {
  return await apiClient.get<Achievement[]>('/api/clanachievement/active');
};


export const createClanAchievementWithFile = async (formData: FormData): Promise<Achievement> => {
  return await apiClient.postFormData<Achievement>('/api/clanachievement', formData);
};


export const updateClanAchievementWithFile = async (id: string, formData: FormData): Promise<Achievement> => {
  return await apiClient.putFormData<Achievement>(`/api/clanachievement/${id}`, formData);
};

export const deleteClanAchievement = async (id: string): Promise<void> => {
  await apiClient.delete<void>(`/api/clanachievement/${id}`);
};

export const toggleClanAchievementStatus = async (id: string, isActive: boolean): Promise<void> => {
  await apiClient.patch<void>(`/api/clanachievement/${id}/status`, isActive);
};

export const getClanAchievementsByClanId = async (clanId: string): Promise<Achievement[]> => {
  return await apiClient.get<Achievement[]>(`/api/clanachievement/clan/${clanId}`);
};

// Referee Achievements
export const getRefereeAchievements = async (): Promise<Achievement[]> => {
  return await apiClient.get<Achievement[]>('/api/refereeachievement');
};

export const getActiveRefereeAchievements = async (): Promise<Achievement[]> => {
  return await apiClient.get<Achievement[]>('/api/refereeachievement/active');
};


export const createRefereeAchievementWithFile = async (formData: FormData): Promise<Achievement> => {
  return await apiClient.postFormData<Achievement>('/api/refereeachievement', formData);
};


export const updateRefereeAchievementWithFile = async (id: string, formData: FormData): Promise<Achievement> => {
  return await apiClient.putFormData<Achievement>(`/api/refereeachievement/${id}`, formData);
};

export const deleteRefereeAchievement = async (id: string): Promise<void> => {
  await apiClient.delete<void>(`/api/refereeachievement/${id}`);
};

export const toggleRefereeAchievementStatus = async (id: string, isActive: boolean): Promise<void> => {
  await apiClient.patch<void>(`/api/refereeachievement/${id}/status`, isActive);
};

export const getRefereeAchievementsByRefereeId = async (refereeId: string): Promise<Achievement[]> => {
  return await apiClient.get<Achievement[]>(`/api/refereeachievement/referee/${refereeId}`);
};

// Achievement Progress
export const getClanProgressByClanId = async (clanId: string): Promise<ClanAchievementProgress[]> => {
  return await apiClient.get<ClanAchievementProgress[]>(`/api/clanachievement/clan/${clanId}/progress`);
};

// Referee Achievement Progress  
export const getRefereeProgress = async (refereeId: string): Promise<AchievementProgress[]> => {
  return await apiClient.get<AchievementProgress[]>(`/api/refereeachievement/referee/${refereeId}/progress`);
};